import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';

import { requestApi } from 'api/Api';

import { formatDate } from 'lib/helpers/dateUtils';
import useTimezone from 'lib/hooks/useTimezone';

import Button from 'components/shared/Button/Button';
import Loading from 'components/shared/Loading/Loading';
import SignatureModal from 'components/shared/Modal/SignatureModal/SignatureModal';

export default function TechnologyConsentForm({ hideTechnologyModal, updateSurcharge, practice }) {
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState(null);
  const sigPad = useRef();
  const divToPrint = useRef();
  const fullFormRef = useRef();
  const timezone = useTimezone();
  const today = formatDate(new Date(), timezone, 'MM/dd/yyyy');
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);

  const hideSignatureModal = () => {
    setShowSignatureModal(false);
  };

  const trimSignature = () => {
    setSignature(sigPad.current.getTrimmedCanvas().toDataURL('image/png'));
  };

  const savePDF = async (handle) => {
    setLoader(true);
    try {
      await requestApi({
        url: '/api/practice/update',
        params: {
          fields: { technology_fee_consent: handle }
        },
        navigate
      });
      updateSurcharge();
      hideTechnologyModal();
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: TechnologyConsentForm, method: savePDF, error: ${error}`
      );
      console.error(error);
    }
    setLoader(false);
  };

  return loader ? (
    <Loading />
  ) : (
    <div className="border-slate-200 mx-auto w-full max-w-[640px] rounded-2xl border bg-white p-8 shadow-xl">
      <div
        ref={fullFormRef}
        className="text-slate-700 max-h-[60vh] space-y-8 overflow-y-auto pr-2 text-sm leading-relaxed">
        <div className="border-slate-100 border-b pb-4">
          <h3 className="text-slate-900 text-center text-lg font-600">
            Myriad Health LLC Miscellaneous Fee Calculator Compliance Agreement
          </h3>
        </div>
        <div>
          <p>
            Client has requested the ”Myriad Health LLC Miscellaneous Fee Calculator” add on
            feature. Client agrees to use the Miscellaneous Fee Calculator in compliance with any
            and all applicable federal, state, and local laws. Use of the Miscellaneous Fee
            Calculator in violation of any federal, state, or local laws, applicable health
            insurance company policies, or Payment Network rules may result in fines, penalties,
            non-payment or transaction chargebacks. Client understands that Myriad Health LLC is not
            responsible for any such violations of law, health insurance company policies, or
            Payment Network rules with respect to Client's use of Miscellaneous Fee Calculator,
            which may be switched on or off at the Client's discretion. Client understands that the
            Miscellaneous Fee Calculator can be adjusted or turned off at Client's discretion.
            Client understands and agrees that this fee is applied as a cost of doing business to{' '}
            ALL transaction types offered; client will not apply this fee to only credit/debit
            transactions.{' '}
          </p>
        </div>
        <div>
          <div className="border-slate-300 bg-slate-50 rounded-xl border border-dashed px-6 py-4">
            <p className="text-slate-800 flex flex-col gap-1 text-sm">
              <em className="font-bold">Client Business name: {practice.name}</em>
              <em className="font-bold">Date: {today}</em>
              <em className="font-bold">Signature:</em>
            </p>
          </div>
        </div>
      </div>
      <div className="border-slate-100 mt-8 border-t pt-6">
        <div className="flex items-center justify-between gap-6">
          {signature ? (
            <div className="flex flex-1 flex-col">
              <span className="text-slate-500 mb-2 text-xs font-semibold uppercase tracking-wide">
                Authorized signatory
              </span>
              <div className="border-slate-200 bg-slate-50 rounded-lg border px-4 py-3">
                <img src={signature} ref={divToPrint} className="max-h-24 object-contain" />
              </div>
            </div>
          ) : (
            <div className="flex w-full justify-end">
              <Button text="Click to sign" onClick={() => setShowSignatureModal(true)} />
            </div>
          )}
        </div>
      </div>
      <SignatureModal
        showSignatureModal={showSignatureModal}
        hideSignatureModal={hideSignatureModal}
        signature={signature}
        trimSignature={trimSignature}
        savePDF={savePDF}
        sigPad={sigPad}
        divToPrint={divToPrint}
        fullFormRef={fullFormRef}
        y={140}
        setLoader={setLoader}
      />
    </div>
  );
}
